import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, TrendingUp, TrendingDown, LoaderCircle } from "lucide-react";
import axios from "../axios";

const LOCALES = { en: "en", ar: "ar", ku: "ckb" };
const locale = LOCALES[localStorage.getItem("lang")] || "en";

const formatNumber = (n) => new Intl.NumberFormat(locale).format(n);

// Parses "YYYY-MM" / "YYYY-MM-DD" as a local date so labels don't shift a day in negative UTC offsets.
const parseKey = (key) => {
  const [y, m, d = 1] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const monthLabel = (key, opts = { month: "short" }) =>
  parseKey(key).toLocaleDateString(locale, opts);
const dayLabel = (key) =>
  parseKey(key).toLocaleDateString(locale, { month: "short", day: "numeric" });

// Rounds the axis maximum up to a tidy number so gridlines land on whole values.
const niceMax = (max) => {
  if (max <= 4) return 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
  const step = [1, 2, 2.5, 5, 10].map((s) => s * magnitude).find((s) => s * 4 >= max);
  return step * 4;
};

const W = 640;
const H = 240;
const PAD = { top: 12, right: 12, bottom: 28, left: 40 };
const plotW = W - PAD.left - PAD.right;
const plotH = H - PAD.top - PAD.bottom;

function Grid({ max }) {
  return [0, 1, 2, 3, 4].map((i) => {
    const value = (max / 4) * i;
    const y = PAD.top + plotH - (value / max) * plotH;
    return (
      <g key={i}>
        <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} stroke="#eceef5" />
        <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#7981a1">
          {formatNumber(value)}
        </text>
      </g>
    );
  });
}

function BarChart({ data, label, valueLabel }) {
  const [active, setActive] = useState(null);
  const max = niceMax(Math.max(...data.map((d) => d.count), 0));
  const slot = plotW / data.length;
  const barW = Math.min(slot * 0.62, 36);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={valueLabel}>
      <Grid max={max} />
      {data.map((d, i) => {
        const h = (d.count / max) * plotH;
        const x = PAD.left + slot * i + (slot - barW) / 2;
        const isActive = active === i;
        return (
          <g
            key={d.month}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            onClick={() => setActive(isActive ? null : i)}
          >
            <rect x={PAD.left + slot * i} y={PAD.top} width={slot} height={plotH} fill="transparent" />
            <rect
              x={x}
              y={PAD.top + plotH - h}
              width={barW}
              height={Math.max(h, d.count ? 2 : 0)}
              rx="4"
              fill={isActive ? "#1f47ba" : "#2a5ce6"}
            />
            <text
              x={x + barW / 2}
              y={H - 8}
              textAnchor="middle"
              fontSize="11"
              fill={isActive ? "#1a1f33" : "#7981a1"}
              fontWeight={isActive ? 600 : 400}
            >
              {label(d)}
            </text>
            {isActive && (
              <g>
                <rect
                  x={Math.min(Math.max(x + barW / 2 - 44, PAD.left), W - PAD.right - 88)}
                  y={Math.max(PAD.top + plotH - h - 34, 0)}
                  width="88"
                  height="26"
                  rx="6"
                  fill="#1a1f33"
                />
                <text
                  x={Math.min(Math.max(x + barW / 2, PAD.left + 44), W - PAD.right - 44)}
                  y={Math.max(PAD.top + plotH - h - 17, 17)}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#fff"
                >
                  {monthLabel(d.month, { month: "short", year: "numeric" })}: {formatNumber(d.count)}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AreaChart({ data }) {
  const [active, setActive] = useState(null);
  const max = niceMax(Math.max(...data.map((d) => d.count), 0));
  const step = plotW / (data.length - 1);
  const points = data.map((d, i) => [
    PAD.left + step * i,
    PAD.top + plotH - (d.count / max) * plotH,
  ]);
  const line = points.map(([x, y], i) => `${i ? "L" : "M"}${x},${y}`).join(" ");
  const area = `${line} L${PAD.left + plotW},${PAD.top + plotH} L${PAD.left},${PAD.top + plotH} Z`;
  const labelEvery = Math.ceil(data.length / 6);

  const move = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    setActive(Math.min(data.length - 1, Math.max(0, Math.round((x - PAD.left) / step))));
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto"
      role="img"
      onMouseMove={move}
      onMouseLeave={() => setActive(null)}
    >
      <defs>
        <linearGradient id="visits-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2a5ce6" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#2a5ce6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <Grid max={max} />
      <path d={area} fill="url(#visits-area)" />
      <path d={line} fill="none" stroke="#2a5ce6" strokeWidth="2.5" strokeLinejoin="round" />
      {data.map(
        (d, i) =>
          i % labelEvery === 0 && (
            <text key={d.date} x={points[i][0]} y={H - 8} textAnchor="middle" fontSize="11" fill="#7981a1">
              {dayLabel(d.date)}
            </text>
          )
      )}
      {active !== null && (
        <g>
          <line x1={points[active][0]} x2={points[active][0]} y1={PAD.top} y2={PAD.top + plotH} stroke="#aab1c9" strokeDasharray="3 3" />
          <circle cx={points[active][0]} cy={points[active][1]} r="4.5" fill="#fff" stroke="#2a5ce6" strokeWidth="2.5" />
          <rect
            x={Math.min(Math.max(points[active][0] - 50, PAD.left), W - PAD.right - 100)}
            y={Math.max(points[active][1] - 38, 0)}
            width="100"
            height="26"
            rx="6"
            fill="#1a1f33"
          />
          <text
            x={Math.min(Math.max(points[active][0], PAD.left + 50), W - PAD.right - 50)}
            y={Math.max(points[active][1] - 21, 17)}
            textAnchor="middle"
            fontSize="12"
            fill="#fff"
          >
            {dayLabel(data[active].date)}: {formatNumber(data[active].count)}
          </text>
        </g>
      )}
    </svg>
  );
}

function StatCard({ title, value, children }) {
  return (
    <div className="bg-white rounded-2xl border border-ink-100 shadow-sm p-5">
      <p className="text-sm text-ink-500">{title}</p>
      <p className="mt-1 text-3xl font-display font-bold text-ink-900">{value}</p>
      {children && <div className="mt-1 text-sm">{children}</div>}
    </div>
  );
}

function LinkClicksPanel({ links }) {
  const { t } = useTranslation();
  const sorted = useMemo(() => [...links].sort((a, b) => b.clicks - a.clicks), [links]);
  const max = Math.max(...sorted.map((l) => l.clicks), 1);

  if (sorted.length === 0) {
    return (
      <Panel title={t("Clicks by link")}>
        <p className="text-ink-500">{t("Add a link to start seeing clicks here.")}</p>
      </Panel>
    );
  }

  return (
    <Panel title={t("Clicks by link")} subtitle={t("Total clicks per link")}>
      <ul className="space-y-3">
        {sorted.map((link) => (
          <li key={link.id}>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-ink-900 truncate">{link.social_name}</span>
              <span className="text-ink-500 shrink-0">{formatNumber(link.clicks)}</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-ink-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-brand-600"
                style={{ width: `${(link.clicks / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <section className="bg-white rounded-2xl border border-ink-100 shadow-sm p-5 md:p-6">
      <h2 className="text-lg font-display font-semibold text-ink-900">{title}</h2>
      {subtitle && <p className="text-sm text-ink-500 mt-0.5">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function Stats() {
  const { t } = useTranslation();
  const authed = !!localStorage.getItem("token");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [linkClicks, setLinkClicks] = useState(null);

  useEffect(() => {
    if (!authed) return;
    axios
      .get("/stats/visits")
      .then(({ data }) => setStats(data))
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("token");
          window.location.href = "/en/setup";
        } else {
          setError(true);
        }
      });
    axios
      .get("/stats/clicks")
      .then(({ data }) => setLinkClicks(data.links))
      .catch(() => {});
  }, [authed]);

  const insights = useMemo(() => {
    if (!stats) return null;
    const months = stats.visits_by_month;
    // Months before the first visit aren't "quiet", they just predate the profile, so keep them out of the average.
    const firstKey = stats.first_visit_at?.slice(0, 7);
    const active = firstKey ? months.filter((m) => m.month >= firstKey) : [];
    const best = months.reduce((a, b) => (b.count > a.count ? b : a), months[0]);
    const change =
      stats.last_month > 0
        ? Math.round(((stats.this_month - stats.last_month) / stats.last_month) * 100)
        : null;
    return {
      best: best.count > 0 ? best : null,
      average: active.length ? Math.round(active.reduce((s, m) => s + m.count, 0) / active.length) : 0,
      change,
    };
  }, [stats]);

  if (!authed) return <Navigate replace to="/en/setup" />;

  return (
    <main className="bg-ink-50 min-h-[calc(100vh-5rem)]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Link
          to="/en/setup"
          className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-brand-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("Back to my page")}
        </Link>
        <h1 className="mt-3 text-3xl md:text-4xl font-display font-bold text-ink-900">
          {t("Page statistics")}
        </h1>
        <p className="mt-1 text-ink-500">{t("See how many people have visited your page.")}</p>

        {error && (
          <p className="mt-10 text-accent-600" role="alert">
            {t("Something went wrong")}
          </p>
        )}

        {!stats && !error && (
          <div className="mt-16 flex justify-center text-brand-600">
            <LoaderCircle className="w-8 h-8 animate-spin" aria-label={t("Loading...")} />
          </div>
        )}

        {stats && (
          <div className="mt-8 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title={t("Total visits")} value={formatNumber(stats.total)} />
              <StatCard title={t("Last 7 days")} value={formatNumber(stats.last_7_days)} />
              <StatCard title={t("Last 30 days")} value={formatNumber(stats.last_30_days)} />
              <StatCard title={t("This month")} value={formatNumber(stats.this_month)}>
                {insights.change === null ? (
                  <span className="text-ink-400">{t("No visits last month")}</span>
                ) : (
                  <span
                    className={`inline-flex items-center gap-1 font-medium ${
                      insights.change >= 0 ? "text-emerald-600" : "text-accent-600"
                    }`}
                  >
                    {insights.change >= 0 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    <span dir="ltr">{insights.change > 0 ? "+" : ""}{insights.change}%</span>
                    <span className="text-ink-400 font-normal">{t("vs last month")}</span>
                  </span>
                )}
              </StatCard>
            </div>

            {stats.total === 0 ? (
              <Panel title={t("No visits yet")}>
                <p className="text-ink-500">
                  {t("Share your page link to start seeing visits here.")}
                </p>
              </Panel>
            ) : (
              <>
                <Panel title={t("Visits per month")} subtitle={t("Last 12 months")}>
                  <BarChart
                    data={stats.visits_by_month}
                    label={(d) => monthLabel(d.month)}
                    valueLabel={t("Visits per month")}
                  />
                </Panel>

                <div className="grid md:grid-cols-3 gap-4">
                  <StatCard
                    title={t("Best month")}
                    value={insights.best ? formatNumber(insights.best.count) : "—"}
                  >
                    {insights.best && (
                      <span className="text-ink-500">
                        {monthLabel(insights.best.month, { month: "long", year: "numeric" })}
                      </span>
                    )}
                  </StatCard>
                  <StatCard title={t("Monthly average")} value={formatNumber(insights.average)} />
                  <StatCard title={t("Visits today")} value={formatNumber(stats.today)} />
                </div>

                <Panel title={t("Daily visits")} subtitle={t("Last 30 days")}>
                  <AreaChart data={stats.visits_by_day} />
                </Panel>
              </>
            )}

            {linkClicks && <LinkClicksPanel links={linkClicks} />}
          </div>
        )}
      </div>
    </main>
  );
}
